/**
 * Win-back reminder card (sections/tf-winback.liquid).
 *
 * Two notes in localStorage say a shopper left pieces behind:
 *   tf_winback_cart      { t }                    set here when the cart first gets a piece, and again when a
 *                                                 Checkout button is clicked
 *   tf_winback_checkout  { t, cid, items, ... }   set by the "TF win-back" custom pixel; removed when an order completes
 * The card shows once per visit, from `delayHours` after the latest note until 7 days, at most on 2 visits.
 * If the cart has since been emptied (bought, or cleared by the shopper), the notes are dropped.
 */
(() => {
  const CART_KEY = 'tf_winback_cart';
  const CHECKOUT_KEY = 'tf_winback_checkout';
  const SEEN_KEY = 'tf_winback_seen'; // { t, n }: visits the card was shown for the note at time t
  const MAX_AGE = 7 * 24 * 3600e3;
  const MAX_VISITS = 2;
  const ATTRIBUTE = 'Came back via reminder popup';

  const read = (key) => {
    try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
  };
  const write = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
  };
  const forget = () => {
    try { [CART_KEY, CHECKOUT_KEY, SEEN_KEY].forEach((k) => localStorage.removeItem(k)); } catch {}
  };

  // Remember checkout clicks on the cart page and in the cart drawer.
  document.addEventListener('click', (e) => {
    if (e.target.closest?.('[name="checkout"], a[href^="/checkout"]')) write(CART_KEY, { t: Date.now() });
  }, true);

  // Remember when the cart first gets a piece, so shoppers who never reach checkout are reminded too.
  import('@shopify/events').then(({ StandardEvents }) => {
    document.addEventListener(StandardEvents.cartLinesUpdate, (e) => {
      e.promise?.then(({ cart, detail }) => {
        const count = cart?.totalQuantity ?? detail?.itemCount ?? 0;
        if (count === 0) forget();
        else if (!read(CART_KEY)) write(CART_KEY, { t: Date.now() });
      }).catch(() => {});
    });
  }).catch(() => {});

  const card = document.getElementById('tf-winback');
  const dataEl = document.getElementById('tf-winback-data');
  if (!card || !dataEl) return;
  const data = JSON.parse(dataEl.textContent);
  const preview = new URLSearchParams(location.search).get('tf_winback') === 'preview';

  let cartNote = read(CART_KEY);
  const checkoutNote = read(CHECKOUT_KEY);

  // An empty cart after a cart note means it was bought or cleared: nothing to remind about.
  if (cartNote && data.cartCount === 0) return forget();
  // A cart with pieces but no note (filled before this was added, or the add wasn't seen): start counting now.
  if (!cartNote && data.cartCount > 0) {
    cartNote = { t: Date.now() };
    write(CART_KEY, cartNote);
  }

  const noteTime = Math.max(cartNote?.t || 0, checkoutNote?.t || 0);
  if (!preview) {
    if (!data.enabled || !noteTime) return;
    const age = Date.now() - noteTime;
    if (age > MAX_AGE) return forget();
    if (age < data.delayHours * 3600e3) return;
    try { if (sessionStorage.getItem(SEEN_KEY)) return; } catch {}
    const seen = read(SEEN_KEY);
    if (seen?.t === noteTime && seen.n >= MAX_VISITS) return;
  }

  const money = (amount, currency) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: currency || data.currency || 'AUD' }).format(amount);

  // Pieces to show: what's in the cart now, plus pieces from the checkout note that aren't (e.g. "Buy it now").
  async function gather() {
    const cart = await fetch('/cart.js').then((r) => r.json());
    const items = cart.items.map((i) => ({
      v: String(i.variant_id), q: i.quantity, title: i.product_title,
      variant: i.variant_title || '', img: i.image, url: i.url, price: i.final_line_price / 100, inCart: true,
    }));
    const missing = (checkoutNote?.items || []).filter((n) => !items.some((i) => i.v === String(n.v)));
    const available = await Promise.all(missing.map(async (n) => {
      try {
        const path = new URL(n.url, location.origin).pathname;
        const product = await fetch(`${path}.js`).then((r) => r.json());
        return product.variants.some((v) => String(v.id) === String(n.v) && v.available);
      } catch { return false; }
    }));
    missing.forEach((n, i) => {
      if (available[i]) items.push({ ...n, price: n.price * n.q, inCart: false });
    });
    return { items, currency: cart.currency };
  }

  function render(items, currency) {
    const list = card.querySelector('[data-tf-winback-items]');
    const shown = items.slice(0, 3);
    list.innerHTML = '';
    for (const item of shown) {
      const li = document.createElement('li');
      const img = document.createElement('img');
      img.src = item.img ? `${item.img}${item.img.includes('?') ? '&' : '?'}width=120` : '';
      img.alt = '';
      img.width = 56;
      img.height = 56;
      img.loading = 'lazy';
      const text = document.createElement('span');
      const title = document.createElement('a');
      title.href = item.url || '#';
      title.textContent = item.title;
      text.append(title);
      if (item.variant) text.append(Object.assign(document.createElement('small'), { textContent: item.variant }));
      const price = Object.assign(document.createElement('b'), {
        textContent: (item.q > 1 ? `${item.q} × ` : '') + money(item.price, currency),
      });
      li.append(img, text, price);
      list.append(li);
    }
    if (items.length > shown.length) {
      list.append(Object.assign(document.createElement('li'), {
        className: 'tf-winback__more', textContent: `+ ${items.length - shown.length} more`,
      }));
    }
  }

  // The Shopify Forms "10% off" tab waits while the card is open.
  const forms = () => document.querySelectorAll('shopify-forms-embed');
  function open() {
    forms().forEach((el) => (el.style.visibility = 'hidden'));
    card.hidden = false;
    requestAnimationFrame(() => card.classList.add('is-open'));
  }
  function close() {
    card.classList.remove('is-open');
    card.hidden = true;
    forms().forEach((el) => (el.style.visibility = ''));
  }

  card.querySelectorAll('[data-tf-winback-close]').forEach((b) => b.addEventListener('click', () => {
    close();
    if (!preview) forget();
  }));
  card.addEventListener('keydown', (e) => { if (e.key === 'Escape') card.querySelector('[data-tf-winback-close]').click(); });

  let pending = [];
  card.querySelector('[data-tf-winback-go]').addEventListener('click', async (e) => {
    e.currentTarget.disabled = true;
    try {
      const add = pending.filter((i) => !i.inCart).map((i) => ({ id: Number(i.v), quantity: i.q }));
      if (add.length) await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: add }) });
      await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { [ATTRIBUTE]: 'Yes' } }) });
      if (!preview) forget();
      location.href = '/checkout';
    } catch {
      location.href = '/cart';
    }
  });

  gather().then(({ items, currency }) => {
    if (!items.length) {
      if (!preview) forget();
      return;
    }
    pending = items;
    render(items, currency);
    setTimeout(open, preview ? 300 : 2500);
    if (!preview) {
      const seen = read(SEEN_KEY);
      write(SEEN_KEY, { t: noteTime, n: seen?.t === noteTime ? seen.n + 1 : 1 });
      try { sessionStorage.setItem(SEEN_KEY, '1'); } catch {}
    }
  }).catch(() => {});
})();
